import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * NEBULA — Astronomer's Star Map
 *
 * Deep midnight indigo + silver + gold star accent. A premium wedding
 * invitation rendered as a 19th-century astronomer's chart. Star
 * catalogs, celestial coordinates, constellations that DRAW themselves.
 *
 *  ACT I   HERO — Star catalog metadata top (RA/Dec coordinates),
 *          title displayed below a constellation chart that draws
 *          itself: stars appear → lines connect them → labels fade in.
 *          Multi-layer parallax star field background.
 *  ACT II  PINNED CONSTELLATION REVEAL — Big SVG constellation
 *          fills viewport. As you scroll, stars POP in one-by-one,
 *          then connecting lines DRAW (stroke-dashoffset), forming
 *          three figures successively: HEART → KEY → CROWN.
 *  ACT III STAR CATALOG TABLE — Story written as a star catalog
 *          (HD numbers, magnitudes, declinations) with mono font.
 *  ACT IV  ASTROLABE DATE — Date inside a rotating astrolabe device
 *          with concentric rings of zodiac, days, hours. Rings rotate.
 *  ACT V   ORBITAL GALLERY — 5 photos in elliptical orbit around a
 *          center "sun", each orbits at its own rate (continuous).
 *  ACT VI  STARLIGHT RSVP — RSVP card with constellation form,
 *          magnetic 3D tilt, gold corner stars.
 */
export function NebulaLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const starFieldRef = useRef<HTMLCanvasElement>(null);
  const cursorStarRef = useRef<HTMLCanvasElement>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const monthName = d?.toLocaleString("tr-TR", { month: "long" }) ?? "—";
  const year = d?.getFullYear() ?? 2026;

  // ── Background twinkling star field ──────────────────────────────
  useEffect(() => {
    const canvas = starFieldRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const stars: { x: number; y: number; r: number; baseOpacity: number; opacity: number; pulseSpeed: number; pulse: number; layer: number }[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // 3 parallax layers of stars
    for (let i = 0; i < 250; i++) {
      const layer = Math.floor(Math.random() * 3);
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: (3 - layer) * 0.5 + Math.random() * 0.4,
        baseOpacity: (3 - layer) / 3 * 0.7 + Math.random() * 0.3,
        opacity: 0,
        pulseSpeed: Math.random() * 0.02 + 0.005,
        pulse: Math.random() * Math.PI * 2,
        layer,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const time = Date.now() * 0.001;
      stars.forEach((s) => {
        s.pulse += s.pulseSpeed;
        s.opacity = s.baseOpacity * (Math.sin(time + s.pulse) * 0.3 + 0.7);

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(244, 232, 200, ${s.opacity})`;
        ctx.fill();

        // Larger stars get a subtle cross glint
        if (s.r > 1) {
          ctx.save();
          ctx.globalAlpha = s.opacity * 0.5;
          ctx.strokeStyle = "rgba(244, 232, 200, 0.6)";
          ctx.lineWidth = 0.3;
          ctx.beginPath();
          ctx.moveTo(s.x - s.r * 3, s.y);
          ctx.lineTo(s.x + s.r * 3, s.y);
          ctx.moveTo(s.x, s.y - s.r * 3);
          ctx.lineTo(s.x, s.y + s.r * 3);
          ctx.stroke();
          ctx.restore();
        }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ── Cursor leaves star trail ─────────────────────────────────────
  useEffect(() => {
    const canvas = cursorStarRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const trail: { x: number; y: number; r: number; opacity: number }[] = [];
    let lastDrop = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const handleMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastDrop > 70) {
        lastDrop = now;
        trail.push({
          x: e.clientX + (Math.random() - 0.5) * 20,
          y: e.clientY + (Math.random() - 0.5) * 20,
          r: Math.random() * 1.5 + 0.5,
          opacity: 0.9,
        });
        if (trail.length > 30) trail.shift();
      }
    };
    window.addEventListener("mousemove", handleMove);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      trail.forEach((s) => {
        s.opacity -= 0.008;
        if (s.opacity > 0) {
          // Twinkle star with glow
          const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 6);
          grad.addColorStop(0, `rgba(255, 220, 130, ${s.opacity * 0.5})`);
          grad.addColorStop(1, "rgba(255, 220, 130, 0)");
          ctx.fillStyle = grad;
          ctx.fillRect(s.x - s.r * 6, s.y - s.r * 6, s.r * 12, s.r * 12);
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 240, 200, ${s.opacity})`;
          ctx.fill();
        }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMove);
    };
  }, []);

  // Magnetic tilt on RSVP
  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.6, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    // Helper: prepare SVG line for draw-in via strokeDashoffset
    // (drawSVG plugin is paid; this is a free equivalent for straight lines)
    const prepLines = (selector: string) => {
      document.querySelectorAll<SVGLineElement>(selector).forEach((line) => {
        const x1 = parseFloat(line.getAttribute("x1") || "0");
        const y1 = parseFloat(line.getAttribute("y1") || "0");
        const x2 = parseFloat(line.getAttribute("x2") || "0");
        const y2 = parseFloat(line.getAttribute("y2") || "0");
        const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) || 1;
        line.style.strokeDasharray = `${len}`;
        line.style.strokeDashoffset = `${len}`;
        (line as any).dataset.len = `${len}`;
      });
    };
    prepLines(".neb-hero-line");
    prepLines(".neb-c-line");

    const ctx = gsap.context(() => {
      // ── ACT I: HERO — constellation draws itself on load ───────────
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(".neb-marquee-t", { x: "100%", duration: 1.6 }, 0);
      tl.from(".neb-marquee-b", { x: "-100%", duration: 1.6 }, 0);

      // 1. Stars pop in
      tl.from(".neb-hero-star", { scale: 0, opacity: 0, stagger: { each: 0.05, from: "random" }, duration: 0.6, ease: "back.out(1.8)" }, 0.5);

      // 2. Connecting lines draw (strokeDashoffset → 0)
      tl.to(".neb-hero-line", { strokeDashoffset: 0, stagger: 0.04, duration: 0.9, ease: "power2.inOut" }, 1.2);

      // 3. Title characters appear under constellation
      const titleSplit = new SplitText(".neb-hero-title", { type: "chars" });
      tl.from(titleSplit.chars, { y: 30, opacity: 0, stagger: 0.04, duration: 1, ease: "power3.out" }, 1.8);

      tl.from(".neb-hero-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 0.9 }, 2.2);

      // Subtle continuous pulse on all stars
      gsap.to(".neb-hero-star", {
        scale: 1.4,
        duration: 1.6,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        stagger: { each: 0.1, from: "random", repeat: -1 },
      });

      // ── ACT II: PINNED CONSTELLATION REVEAL ─────────────────────────
      const constSection = document.querySelector<HTMLElement>(".neb-const-section");
      if (constSection) {
        const stageIndicator = document.querySelector<HTMLElement>(".neb-stage-label");
        const stages = [
          { label: "I · The Heart", group: ".neb-const-heart" },
          { label: "II · The Key",  group: ".neb-const-key" },
          { label: "III · The Crown", group: ".neb-const-crown" },
        ];

        const constTl = gsap.timeline({
          scrollTrigger: {
            trigger: constSection,
            start: "top top",
            end: "+=400%",
            pin: true,
            scrub: 1,
            onUpdate(self) {
              if (stageIndicator) {
                const stage = Math.min(2, Math.floor(self.progress * 3));
                stageIndicator.textContent = stages[stage].label;
              }
            },
          },
        });

        // Each constellation group: stars pop → lines draw → fade out
        stages.forEach((s, i) => {
          const start = i * 1.2;
          constTl.from(`${s.group} .neb-c-star`, {
            scale: 0,
            opacity: 0,
            stagger: 0.06,
            duration: 0.4,
            ease: "back.out(1.8)",
          }, start);
          constTl.fromTo(`${s.group} .neb-c-line`,
            { strokeDashoffset: (i, t) => parseFloat((t as any).dataset?.len || "200") },
            { strokeDashoffset: 0, stagger: 0.04, duration: 0.4, ease: "power2.out" },
            start + 0.3);
          // Fade out before next
          if (i < stages.length - 1) {
            constTl.to(`${s.group}`, { opacity: 0, duration: 0.3 }, start + 1.0);
          }
        });
      }

      // ── ACT III: STAR CATALOG TABLE ─────────────────────────────────
      gsap.from(".neb-cat-row", { x: -40, opacity: 0, stagger: 0.08, duration: 1, ease: "expo.out",
        scrollTrigger: { trigger: ".neb-cat-section", start: "top 75%", toggleActions: "play reverse play reverse" } });
      gsap.from(".neb-cat-rule", { scaleX: 0, transformOrigin: "left", duration: 1.4, ease: "expo.inOut",
        scrollTrigger: { trigger: ".neb-cat-section", start: "top 70%", toggleActions: "play reverse play reverse" } });

      // ── ACT IV: ASTROLABE DATE ──────────────────────────────────────
      gsap.from(".neb-astro", { scale: 0.85, opacity: 0, duration: 1.6, ease: "expo.out",
        scrollTrigger: { trigger: ".neb-date-section", start: "top 70%", toggleActions: "play reverse play reverse" } });

      // Each ring rotates continuously at different rates
      gsap.to(".neb-ring-outer", { rotate: 360, duration: 80, ease: "linear", repeat: -1, transformOrigin: "50% 50%" });
      gsap.to(".neb-ring-mid", { rotate: -360, duration: 50, ease: "linear", repeat: -1, transformOrigin: "50% 50%" });
      gsap.to(".neb-ring-inner", { rotate: 360, duration: 30, ease: "linear", repeat: -1, transformOrigin: "50% 50%" });

      // Date entry
      gsap.from(".neb-date-text", { y: 20, opacity: 0, stagger: 0.12, duration: 1, ease: "expo.out",
        scrollTrigger: { trigger: ".neb-date-section", start: "top 65%", toggleActions: "play reverse play reverse" } });

      // ── ACT V: ORBITAL GALLERY ──────────────────────────────────────
      // 5 photos orbit around center sun at different rates
      document.querySelectorAll(".neb-orbit").forEach((orbit, i) => {
        gsap.to(orbit, {
          rotate: 360,
          duration: 40 + i * 12,
          ease: "linear",
          repeat: -1,
          transformOrigin: "50% 50%",
        });
        // The photo inside the orbit counter-rotates so it stays upright-ish
        const photo = orbit.querySelector(".neb-orbit-photo");
        if (photo) {
          gsap.to(photo, {
            rotate: -360,
            duration: 40 + i * 12,
            ease: "linear",
            repeat: -1,
            transformOrigin: "50% 50%",
          });
        }
      });

      gsap.from(".neb-orbit-title", { scale: 0.7, opacity: 0, duration: 1.6, ease: "expo.out",
        scrollTrigger: { trigger: ".neb-orbit-section", start: "top 70%", toggleActions: "play reverse play reverse" } });
      gsap.from(".neb-orbit", { opacity: 0, scale: 0, stagger: 0.15, duration: 1.4, ease: "back.out(1.5)",
        scrollTrigger: { trigger: ".neb-orbit-section", start: "top 65%", toggleActions: "play reverse play reverse" } });

      // Form
      gsap.from(".neb-form-card", { opacity: 0, y: 60, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".neb-form-card", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // Hero constellation — 8 stars + lines forming an abstract shape
  const heroStars = [
    { x: 100, y: 80 }, { x: 200, y: 50 }, { x: 320, y: 100 },
    { x: 250, y: 180 }, { x: 150, y: 200 }, { x: 80, y: 160 },
    { x: 380, y: 200 }, { x: 220, y: 270 },
  ];
  const heroLines: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0],
    [2, 6], [6, 3], [3, 7], [7, 4],
  ];

  // Constellation shapes for Act II — HEART, KEY, CROWN
  // Each is a list of star positions + connecting lines
  const constHeart = {
    stars: [
      { x: 200, y: 90 }, { x: 280, y: 60 }, { x: 350, y: 120 },
      { x: 370, y: 200 }, { x: 320, y: 280 }, { x: 200, y: 340 },
      { x: 80, y: 280 }, { x: 30, y: 200 }, { x: 50, y: 120 }, { x: 120, y: 60 },
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 0]] as [number, number][],
  };
  const constKey = {
    stars: [
      { x: 80, y: 60 }, { x: 140, y: 80 }, { x: 160, y: 140 }, { x: 110, y: 200 },
      { x: 160, y: 240 }, { x: 220, y: 240 }, { x: 260, y: 200 }, { x: 260, y: 140 },
      { x: 320, y: 200 }, { x: 360, y: 140 },
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [5, 6], [6, 7], [7, 1], [6, 8], [8, 9]] as [number, number][],
  };
  const constCrown = {
    stars: [
      { x: 60, y: 240 }, { x: 100, y: 120 }, { x: 160, y: 80 },
      { x: 200, y: 60 }, { x: 240, y: 80 }, { x: 300, y: 120 },
      { x: 340, y: 240 }, { x: 280, y: 240 }, { x: 200, y: 240 }, { x: 120, y: 240 },
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 0]] as [number, number][],
  };

  const renderConst = (cls: string, c: { stars: { x: number; y: number }[]; lines: [number, number][] }, accent: string) => (
    <g className={`${cls} neb-const-figure`}>
      {c.lines.map(([a, b], i) => (
        <line
          key={i}
          className="neb-c-line"
          x1={c.stars[a].x} y1={c.stars[a].y}
          x2={c.stars[b].x} y2={c.stars[b].y}
          stroke={accent}
          strokeWidth="0.6"
          opacity="0.8"
        />
      ))}
      {c.stars.map((s, i) => (
        <g key={i} className="neb-c-star">
          <circle cx={s.x} cy={s.y} r="8" fill={accent} opacity="0.15" />
          <circle cx={s.x} cy={s.y} r="3.5" fill="#fef9e7" style={{ filter: `drop-shadow(0 0 4px ${accent})` }} />
        </g>
      ))}
    </g>
  );

  return (
    <main className="relative overflow-x-hidden text-white" style={{ background: "#080a1c", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');
        @keyframes nebMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes nebMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* Deep midnight gradient */}
      <div className="fixed inset-0 -z-30 pointer-events-none" style={{
        background: "radial-gradient(ellipse at top, #1a1e3d 0%, #080a1c 60%), radial-gradient(ellipse at bottom, #1f0e2a 0%, transparent 70%)",
      }} />

      {/* Star field canvas */}
      <canvas ref={starFieldRef} className="fixed inset-0 pointer-events-none z-0" />

      {/* Cursor star trail */}
      <canvas ref={cursorStarRef} className="fixed inset-0 pointer-events-none z-0 hidden md:block" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 px-8 py-4 flex justify-between items-center text-[10px] uppercase tracking-[0.5em]"
        style={{ background: "rgba(8, 10, 28, 0.7)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(244, 217, 119, 0.25)" }}>
        <span style={{ color: "#f4d977" }}>Nebula · {year}</span>
        <span className="italic normal-case text-base tracking-normal text-white/70" style={{ fontFamily: "'Cormorant Garamond', serif" }}>~ Ad astra ~</span>
        <span style={{ color: "#f4d977" }}>HD {Math.abs(Number(year) * 7 + 4) % 99999}</span>
      </header>

      {/* ═══ ACT I: HERO ═══════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden z-10">
        <div className="neb-marquee-t relative h-10 border-b border-[#f4d977]/20 overflow-hidden flex items-center mt-12">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#f4d977", animation: "nebMQ_L 38s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                ☆ {title.toUpperCase()} ☆ AD ASTRA PER ASPERA ☆ RA 13h 47m · DEC +14° 32' ☆ {monthShort} {day} {year} ☆
              </span>
            ))}
          </div>
        </div>

        {/* Center stage */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative">
          {/* Constellation SVG above title */}
          <svg viewBox="0 0 460 320" className="w-full max-w-md mb-12" style={{ overflow: "visible" }}>
            {heroLines.map(([a, b], i) => (
              <line
                key={i}
                className="neb-hero-line"
                x1={heroStars[a].x} y1={heroStars[a].y}
                x2={heroStars[b].x} y2={heroStars[b].y}
                stroke="#f4d977"
                strokeWidth="0.5"
                opacity="0.6"
              />
            ))}
            {heroStars.map((s, i) => (
              <g key={i} className="neb-hero-star">
                <circle cx={s.x} cy={s.y} r="10" fill="#f4d977" opacity="0.15" />
                <circle cx={s.x} cy={s.y} r="3" fill="#fef9e7" style={{ filter: "drop-shadow(0 0 4px #f4d977)" }} />
                {/* Star catalog label */}
                <text x={s.x + 8} y={s.y - 6} style={{ fill: "rgba(244, 217, 119, 0.5)", fontSize: 8, fontFamily: "monospace", letterSpacing: 1 }}>
                  α{i + 1}
                </text>
              </g>
            ))}
          </svg>

          <p className="neb-hero-meta text-[10px] uppercase tracking-[0.6em] mb-4" style={{ color: "#f4d977" }}>— Caelum Nuptialis —</p>

          <h1 className="neb-hero-title text-center font-light italic leading-[0.9] tracking-[-0.02em]"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "clamp(3.5rem, 11vw, 9rem)",
              color: "#fef9e7",
              textShadow: "0 0 80px rgba(244, 217, 119, 0.2)",
            }}>
            {title}
          </h1>

          <div className="neb-hero-meta mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.5em] text-white/60">
            <span>{monthShort}</span>
            <span style={{ color: "#f4d977" }}>✦</span>
            <span className="tabular-nums">{day}</span>
            <span style={{ color: "#f4d977" }}>✦</span>
            <span>{year}</span>
          </div>
          <p className="neb-hero-meta mt-3 italic text-white/50" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}>
            ~ sub stellis ~
          </p>
        </div>

        <div className="neb-marquee-b relative h-10 border-t border-[#f4d977]/20 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase text-white/60" style={{ animation: "nebMQ_R 42s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">✦ MAGNITUDE 1.2 ✦ DISTANCE ∞ ✦ {(location ?? "").toUpperCase()} ✦</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED CONSTELLATION REVEAL ═══════════════════════ */}
      <section className="neb-const-section relative h-screen overflow-hidden z-10">
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#f4d977" }}>II · CONSTELLATIONS</div>
        <div className="absolute top-12 right-12 z-30 text-[10px] uppercase tracking-[0.5em] text-white/40">
          <span className="neb-stage-label">I · The Heart</span>
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 400 400" className="w-[min(80vw,80vh)] h-[min(80vw,80vh)]" style={{ overflow: "visible" }}>
            {/* 3 constellation figures stacked, animated via timeline */}
            {renderConst("neb-const-heart", constHeart, "#f4d977")}
            {renderConst("neb-const-key", constKey, "#c0a5e0")}
            {renderConst("neb-const-crown", constCrown, "#a5d4f4")}
          </svg>
        </div>

        {/* Coordinates display */}
        <div className="absolute bottom-12 left-12 text-[10px] font-mono tracking-widest text-white/40">
          RA 13h 47m 32.5s
        </div>
        <div className="absolute bottom-12 right-12 text-right text-[10px] font-mono tracking-widest text-white/40">
          DEC +14° 32' 18"
        </div>
      </section>

      {/* ═══ ACT III: STAR CATALOG TABLE ═══════════════════════════════ */}
      <section className="neb-cat-section relative py-32 px-6 z-10">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4d977" }}>III · STELLAR CATALOG</p>
          <h2 className="text-center font-light italic leading-tight mb-16" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            Astra Nostra
          </h2>

          {/* Table headers */}
          <div className="grid grid-cols-12 gap-4 pb-3 mb-3 border-b border-[#f4d977]/30 text-[10px] uppercase tracking-[0.3em] font-mono text-[#f4d977]/70">
            <div className="col-span-2">HD No.</div>
            <div className="col-span-3">Designation</div>
            <div className="col-span-5">Description</div>
            <div className="col-span-2 text-right">Mag.</div>
          </div>

          {[
            { hd: "001", desig: "α Primavera", desc: "İlk buluşma — bir bahar akşamı, zaman durdu.", mag: "1.2" },
            { hd: "024", desig: "β Voyager", desc: "Üç şehir, sonsuz hatıra.", mag: "0.8" },
            { hd: "132", desig: "γ Promissio", desc: "Plajda, yıldızlar altında bir tek söz.", mag: "0.4" },
            { hd: "200", desig: "δ Hodie", desc: "Bugün — düğünümüze davet ediyoruz.", mag: "0.1" },
            { hd: "404", desig: "ε Aeternus", desc: "Sonsuza dek beraber.", mag: "-1.0" },
          ].map((r, i) => (
            <div key={i} className="neb-cat-row grid grid-cols-12 gap-4 py-4 border-b border-white/5 font-mono items-baseline">
              <div className="col-span-2 text-[11px] tracking-widest" style={{ color: "#f4d977" }}>HD-{r.hd}</div>
              <div className="col-span-3 italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}>{r.desig}</div>
              <div className="col-span-5 italic text-base text-white/75" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{r.desc}</div>
              <div className="col-span-2 text-right text-[11px] tabular-nums tracking-widest text-white/50">{r.mag}</div>
            </div>
          ))}

          <div className="neb-cat-rule h-px mt-3" style={{ background: "linear-gradient(90deg, transparent, #f4d977, transparent)" }} />

          <p className="text-center mt-8 italic text-lg text-white/60" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            ~ Plate I of I · Observed at {(location ?? "").split(",")[0]} ~
          </p>
        </div>
      </section>

      {/* ═══ ACT IV: ASTROLABE DATE ════════════════════════════════════ */}
      <section className="neb-date-section relative py-32 px-6 z-10">
        <div className="max-w-2xl mx-auto text-center">
          <p className="neb-date-text text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4d977" }}>IV · ASTROLABIUM</p>
          <h2 className="neb-date-text font-light italic leading-tight mb-16" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            The Celestial Day
          </h2>

          {/* Astrolabe */}
          <div className="neb-astro relative mx-auto" style={{ width: 380, height: 380 }}>
            <svg viewBox="0 0 380 380" className="w-full h-full" style={{ overflow: "visible" }}>
              {/* Outer ring — zodiac symbols */}
              <g className="neb-ring-outer">
                <circle cx="190" cy="190" r="180" fill="none" stroke="#f4d977" strokeWidth="0.8" opacity="0.6" />
                <circle cx="190" cy="190" r="170" fill="none" stroke="#f4d977" strokeWidth="0.3" opacity="0.4" />
                {/* 12 zodiac ticks */}
                {Array.from({ length: 12 }).map((_, i) => {
                  const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
                  const symbols = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];
                  const x = 190 + Math.cos(a) * 175;
                  const y = 190 + Math.sin(a) * 175;
                  return <text key={i} x={x} y={y + 4} textAnchor="middle" style={{ fill: "#f4d977", fontSize: 14, opacity: 0.7 }}>{symbols[i]}</text>;
                })}
              </g>

              {/* Middle ring — months */}
              <g className="neb-ring-mid">
                <circle cx="190" cy="190" r="150" fill="none" stroke="#f4d977" strokeWidth="0.5" opacity="0.4" />
                <circle cx="190" cy="190" r="140" fill="none" stroke="#f4d977" strokeWidth="0.3" opacity="0.3" />
                {Array.from({ length: 24 }).map((_, i) => {
                  const a = (i / 24) * Math.PI * 2 - Math.PI / 2;
                  const x1 = 190 + Math.cos(a) * 140;
                  const y1 = 190 + Math.sin(a) * 140;
                  const x2 = 190 + Math.cos(a) * 150;
                  const y2 = 190 + Math.sin(a) * 150;
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f4d977" strokeWidth={i % 6 === 0 ? 1 : 0.4} opacity={0.6} />;
                })}
              </g>

              {/* Inner ring — hours */}
              <g className="neb-ring-inner">
                <circle cx="190" cy="190" r="120" fill="none" stroke="#f4d977" strokeWidth="0.4" opacity="0.4" />
                {Array.from({ length: 24 }).map((_, i) => {
                  const a = (i / 24) * Math.PI * 2 - Math.PI / 2;
                  const x1 = 190 + Math.cos(a) * 115;
                  const y1 = 190 + Math.sin(a) * 115;
                  const x2 = 190 + Math.cos(a) * 120;
                  const y2 = 190 + Math.sin(a) * 120;
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f4d977" strokeWidth={i % 6 === 0 ? 0.8 : 0.3} opacity={0.5} />;
                })}
              </g>

              {/* Central star */}
              <g>
                <circle cx="190" cy="190" r="32" fill="#f4d977" opacity="0.15" />
                <polygon points="190,160 198,182 220,182 202,196 210,220 190,206 170,220 178,196 160,182 182,182" fill="#fef9e7" style={{ filter: "drop-shadow(0 0 8px #f4d977)" }} />
              </g>

              {/* 4 compass markers (cardinal stars) */}
              {[{ a: -Math.PI / 2, label: "N" }, { a: 0, label: "E" }, { a: Math.PI / 2, label: "S" }, { a: Math.PI, label: "W" }].map((p, i) => {
                const x = 190 + Math.cos(p.a) * 100;
                const y = 190 + Math.sin(p.a) * 100;
                return (
                  <g key={i}>
                    <circle cx={x} cy={y} r="3" fill="#f4d977" style={{ filter: "drop-shadow(0 0 4px #f4d977)" }} />
                    <text x={x} y={y + 14} textAnchor="middle" style={{ fill: "#f4d977", fontSize: 9, opacity: 0.6, fontFamily: "monospace", letterSpacing: 1 }}>{p.label}</text>
                  </g>
                );
              })}
            </svg>

            {/* Date in center */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ marginTop: 70 }}>
              <p className="font-light tabular-nums leading-none" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 64, color: "#fef9e7" }}>{day}</p>
              <p className="italic text-base mt-1" style={{ color: "#f4d977" }}>{monthShort}.{year}</p>
            </div>
          </div>

          {/* Coordinates below */}
          <div className="neb-date-text mt-12 grid grid-cols-2 gap-6 max-w-md mx-auto text-[11px] uppercase tracking-[0.4em] font-mono">
            <div className="text-left" style={{ color: "#f4d977" }}>
              <p className="text-white/40 mb-1">Hour</p>
              <p>{d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}</p>
            </div>
            <div className="text-right" style={{ color: "#f4d977" }}>
              <p className="text-white/40 mb-1">Zodiac</p>
              <p>{["♑","♒","♓","♈","♉","♊","♋","♌","♍","♎","♏","♐"][d?.getMonth() ?? 0]}</p>
            </div>
          </div>

          <p className="neb-date-text mt-8 italic text-lg text-white/60" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            ~ destination: {(location ?? "").split(",")[0]} ~
          </p>
        </div>
      </section>

      {/* ═══ ACT V: ORBITAL GALLERY ════════════════════════════════════ */}
      <section className="neb-orbit-section relative h-screen overflow-hidden flex items-center justify-center z-10">
        <div className="absolute top-12 left-12 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#f4d977" }}>V · IN ORBIT</div>
        <div className="absolute top-12 right-12 text-[10px] uppercase tracking-[0.5em] text-white/40">5 SATELLITES</div>

        <div className="relative" style={{ width: "min(85vw, 75vh)", height: "min(85vw, 75vh)" }}>
          {/* Central sun (title) */}
          <div className="neb-orbit-title absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <div className="relative w-32 h-32 rounded-full flex items-center justify-center" style={{
              background: "radial-gradient(circle, #f4d977 0%, #c9a04e 60%, transparent 100%)",
              boxShadow: "0 0 60px rgba(244, 217, 119, 0.5)",
            }}>
              <p className="italic font-light text-[#1a0e08]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1rem, 1.5vw, 1.5rem)" }}>
                {title.replace(" & ", " ✦ ")}
              </p>
            </div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#f4d977" }}>— Sol Nuptialis —</p>
          </div>

          {/* 5 orbits, each at a different radius and tilt */}
          {[
            { radius: 38, tilt: 0 },
            { radius: 32, tilt: 25 },
            { radius: 44, tilt: -15 },
            { radius: 26, tilt: 45 },
            { radius: 40, tilt: -30 },
          ].map((o, i) => (
            <div
              key={i}
              className="absolute inset-0 pointer-events-none"
              style={{ transform: `rotate(${o.tilt}deg)` }}
            >
              {/* Elliptical orbit path */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                <ellipse cx="50" cy="50" rx={o.radius} ry={o.radius * 0.7} fill="none" stroke="#f4d977" strokeWidth="0.15" opacity="0.4" strokeDasharray="0.5,0.5" />
              </svg>

              {/* Orbit rotating container with photo at right edge */}
              <div className="neb-orbit absolute inset-0">
                <div
                  className="absolute"
                  style={{
                    width: 70,
                    height: 90,
                    left: `${50 + o.radius}%`,
                    top: `${50 - 7}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="neb-orbit-photo w-full h-full" style={{
                    background: `url(${galleryImages[i]}) center/cover`,
                    border: "1px solid rgba(244, 217, 119, 0.4)",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.5), 0 0 20px rgba(244, 217, 119, 0.2)",
                  }}>
                    <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ background: "#f4d977", boxShadow: "0 0 8px #f4d977" }} />
                    <div className="absolute -bottom-5 left-0 right-0 text-center text-[8px] uppercase tracking-[0.3em] font-mono" style={{ color: "#f4d977" }}>0{i + 1}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ ACT VI: RSVP ═════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32 z-10" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="neb-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out text-center"
          style={{
            background: "linear-gradient(135deg, rgba(244, 217, 119, 0.06) 0%, rgba(244, 217, 119, 0.02) 100%)",
            border: "1px solid rgba(244, 217, 119, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(244, 217, 119, 0.2), 0 40px 100px rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(20px)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Gold corner stars */}
          {[{ t: 3, l: 3 }, { t: 3, r: 3 }, { b: 3, l: 3 }, { b: 3, r: 3 }].map((p: any, i) => (
            <div key={i} className="absolute" style={{ ...p, fontSize: 16, color: "#f4d977" }}>✦</div>
          ))}

          <p className="text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4d977" }}>VI · RESPONSUM</p>
          <h3 className="font-light italic mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", color: "#fef9e7" }}>
            Reply by starlight
          </h3>
          <p className="italic mb-10" style={{ color: "#f4d977", fontFamily: "'Cormorant Garamond', serif", fontSize: 16 }}>
            ~ ad astra per aspera ~
          </p>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#f4d977" }} />

          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
